import { Breadcrumb, HeaderToolbar } from "@xola/ui-kit";
import {
    CellStyleModule,
    ClientSideRowModelApiModule,
    ClientSideRowModelModule,
    ColumnApiModule,
    ModuleRegistry,
    RenderApiModule,
    RowApiModule,
    RowAutoHeightModule,
    RowStyleModule,
    ScrollApiModule,
    TooltipModule,
    ValidationModule,
} from "ag-grid-community";
import { ColumnMenuModule, TreeDataModule } from "ag-grid-enterprise";
import clsx from "clsx";
import React, { useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import shallow from "zustand/shallow";
import { CreatePurchaseButton } from "../components/Common/CreatePurchaseButton";
import { GiftQrCodeScanner } from "../components/Common/GiftQrCodeScanner";
import { DashboardCalendar } from "../components/Dashboard/Calendar/DashboardCalendar";
import { DashboardList } from "../components/Dashboard/DashboardList";
import { DashboardFilterList } from "../components/Dashboard/Filters/DashboardFilterList";
import { DashboardSearch } from "../components/Dashboard/Search/DashboardSearch";
import { ColumnLayout } from "../components/Layout/ColumnLayout";
import { EmbeddedAppTriggerLocation } from "../embeddedApps/EmbeddedAppTriggerLocation";
import { useAutoFetchCollection } from "../hooks/useAutoFetchCollection";
import { isCalendarView, useDashboardFilterStore } from "../stores/dashboardFilterStore";
import { DashboardGroupBy, DashboardMode, DashboardScope } from "../types/Dashboard";
import { Page } from "./Page";
import { PageNotFound } from "./PageNotFound";
import { DashboardTable } from "@/components/Dashboard/DashboardTable";
import { DashboardModePicker } from "@/components/Dashboard/Filters/DashboardModePicker";
import { SavedViewsButton } from "@/components/Dashboard/Filters/SavedViewsButton";
import { MerchandiseOrdersDashboard } from "@/components/Dashboard/MerchandiseDashboard/MerchandiseOrdersDashboard";
import { getPermissions } from "@/stores/globalStore";
import { getSeller } from "@/utils/getSeller";

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    ValidationModule,
    ColumnMenuModule,
    TreeDataModule,
    CellStyleModule,
    RowStyleModule,
    RowAutoHeightModule,
    TooltipModule,
    ClientSideRowModelApiModule,
    ColumnApiModule,
    RowApiModule,
    ScrollApiModule,
    RenderApiModule,
]);

export const DashboardPage = () => {
    const seller = getSeller();
    const permissions = getPermissions();
    const history = useHistory();
    const [scope, mode] = useDashboardFilterStore((state) => [state.filters.scope, state.filters.mode], shallow);
    const params = useParams<{ timeslotId?: string }>();

    const isRosterVisible = !!params.timeslotId;

    useEffect(() => {
        if (params.timeslotId && mode !== DashboardMode.LIST) {
            useDashboardFilterStore.getState().setFilters({ mode: DashboardMode.LIST });
        }
    }, [params, mode]);

    useEffect(() => {
        // Reset the value to false when the user changes the view or navigates to the dashboard from another page
        useDashboardFilterStore.getState().setIsRealTimeUpdate(false);
    }, [mode, scope]);

    // As long as the Dashboard is displayed, these queries will always be considered as active.
    // Query invalidation for collections bellow will always trigger a refetch, even if the collections are not visible.
    useAutoFetchCollection(["sellers", seller.id, "guides"], null, {
        enabled: permissions.canViewGuides,
    });
    useAutoFetchCollection("resources", null, { enabled: permissions.canViewEquipments });

    const DashboardModeComponent = dashboardModeMap[mode ?? DashboardMode.LIST];

    if (!permissions.canViewDashboard) {
        return <PageNotFound />;
    }

    const handleModeChange = (mode: DashboardMode) => {
        history.push("/dashboard");
        const setFilters = useDashboardFilterStore.getState().setFilters;
        const filters = useDashboardFilterStore.getState().filters;
        // When switching between modes, update scope if required
        if (mode === DashboardMode.EQUIPMENT) {
            // If we are in equipment mode, we don't show unassigned resources
            setFilters({
                mode,
                products: filters.products,
                guide: filters.guide,
                scope: DashboardScope.ASSIGNED,
                resource: { showUnassigned: false, all: filters.resource.all, selected: filters.resource.selected },
            });
            return;
        }
        if (mode === DashboardMode.TABLE) {
            setFilters({
                mode,
                products: filters.products,
                guide: filters.guide,
                scope: DashboardScope.RESERVED,
                resource: filters.resource,
                groupBy: DashboardGroupBy.NONE,
            });
            return;
        }

        setFilters({
            mode,
            products: filters.products,
            guide: filters.guide,
            scope: filters.mode === DashboardMode.EQUIPMENT ? DashboardScope.RESERVED : filters.scope,
            resource: {
                showUnassigned: filters.mode === DashboardMode.EQUIPMENT ? true : filters.resource.showUnassigned,
                all: filters.resource.all,
                selected: filters.resource.selected,
            },
        });
    };

    return (
        <Page
            title="Dashboard"
            className={clsx({
                "print:invisible": isRosterVisible || mode === DashboardMode.TABLE,
                "dashboard-calendar-view": isCalendarView(),
            })}
        >
            <GiftQrCodeScanner />
            <ColumnLayout>
                <HeaderToolbar classNames="print:hidden">
                    <HeaderToolbar.Breadcrumb>
                        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <DashboardModePicker activeItem={mode} onChange={handleModeChange} />
                        </Breadcrumb.Item>
                    </HeaderToolbar.Breadcrumb>
                    {permissions.canSearchPurchaseFromDashboard && <DashboardSearch />}
                    <SavedViewsButton />
                    <EmbeddedAppTriggerLocation id="dashboard.headerBar" />
                    <CreatePurchaseButton label="Purchase" />
                </HeaderToolbar>
                <DashboardFilterList />
                {mode === DashboardMode.TABLE ? (
                    <DashboardTable />
                ) : mode === DashboardMode.MERCHANDISE ? (
                    <ColumnLayout.Fill>{() => <MerchandiseOrdersDashboard />}</ColumnLayout.Fill>
                ) : DashboardModeComponent ? (
                    <ColumnLayout.Fill>{(height) => <DashboardModeComponent height={height} />}</ColumnLayout.Fill>
                ) : null}
            </ColumnLayout>
        </Page>
    );
};

const dashboardModeMap: {
    [key in DashboardMode]: ({ height }: { height: number }) => JSX.Element;
} = {
    [DashboardMode.LIST]: ({ height }) => <DashboardList height={height} />,
    [DashboardMode.DAY]: ({ height }) => <DashboardCalendar height={height} />,
    [DashboardMode.WEEK]: ({ height }) => <DashboardCalendar height={height} />,
    [DashboardMode.MONTH]: ({ height }) => <DashboardCalendar height={height} />,
    [DashboardMode.EQUIPMENT]: ({ height }) => <DashboardCalendar height={height} />,
    [DashboardMode.TABLE]: () => <DashboardTable />,
    [DashboardMode.MERCHANDISE]: () => <MerchandiseOrdersDashboard />,
};
